import { useHover } from '@/hooks';
import {
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  RefObject,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { ChevronLeft, RemoveAllSelected } from '../Icons';
import { InputLabel } from '../InputLabel';

interface DropdownInputProps {
  buttonRef?: RefObject<HTMLButtonElement>;
  inputValue?: string;
  placeholder?: string;
  isOpen?: boolean;
  onClick?: (event: MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  withArrow?: boolean;
  label?: string;
  inputLabelClassName?: string;
  isMultiSelection?: boolean;
  nrOfSelectedOptions?: number;
  onRemoveAllSelectedClick?: () => void;
}

export const DropdownInput = ({
  children,
  buttonRef,
  inputValue,
  placeholder,
  isOpen,
  onClick,
  disabled,
  className,
  withArrow,
  label,
  inputLabelClassName,
  isMultiSelection,
  nrOfSelectedOptions = 0,
  onRemoveAllSelectedClick,
}: PropsWithChildren<DropdownInputProps>): ReactElement => {
  const newRef = useRef(null);
  const ref = buttonRef ?? newRef;
  const { isHovered, reset } = useHover(ref);

  return (
    <div className="flex w-fit flex-col gap-2">
      {label && (
        <InputLabel
          disabled={disabled}
          className={inputLabelClassName}
          label={label}
        />
      )}
      <button
        ref={ref}
        className={twMerge(
          'relative min-w-32 text-sm font-normal',
          isOpen && 'rounded-b-none',
          disabled && 'cursor-default',
          className,
        )}
        onClick={(event) => {
          if (!disabled) {
            reset();
            onClick?.(event);
          }
        }}
      >
        <div
          className={twMerge(
            'flex h-10 items-center justify-between rounded-[5px] border border-gray-100 bg-gray-100 p-2 text-sm font-normal text-gray-400 outline-none',
            (isOpen || isHovered) && 'border-blue-500',
            disabled && 'border-none',
          )}
        >
          <span
            className={twMerge(
              'w-full overflow-hidden text-ellipsis whitespace-nowrap text-left',
              !inputValue && 'italic text-gray-400',
              inputValue && 'text-gray',
              disabled && 'text-gray-300',
            )}
          >
            {inputValue || placeholder}
          </span>
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-1">
              {isMultiSelection && nrOfSelectedOptions > 0 && (
                <div className="flex h-4 min-w-4 justify-center self-end rounded-l-[3px] bg-gray px-1 text-sm font-black text-white">
                  <span className="flex items-center">
                    {nrOfSelectedOptions > 2 ? '+2' : nrOfSelectedOptions}
                  </span>
                </div>
              )}
              {inputValue && onRemoveAllSelectedClick && (
                <RemoveAllSelected
                  className={twMerge(
                    'rounded-[3px] bg-gray',
                    isMultiSelection &&
                      nrOfSelectedOptions > 0 &&
                      'rounded-l-none',
                  )}
                  onClick={(event: MouseEvent) => {
                    event.stopPropagation();
                    onRemoveAllSelectedClick?.();
                  }}
                />
              )}
            </div>
            {withArrow && (
              <ChevronLeft
                className={twMerge(
                  'size-3 -rotate-90 text-gray transition-transform',
                  isOpen && 'rotate-90 text-blue-500',
                  isHovered && 'text-blue-500',
                  disabled && 'text-gray-300',
                )}
              />
            )}
          </div>
        </div>
        {isOpen && children}
      </button>
    </div>
  );
};
